import { Container, Graphics } from 'pixi.js'
import { randomRange } from '../utils/math'

export interface Particle {
  x: number
  y: number
  vx: number
  vy: number
  life: number
  maxLife: number
  size: number
  color: number
  alpha: number
  graphic: Graphics
}

export interface ParticleEmitterConfig {
  x: number
  y: number
  count: number
  spread: number
  speed: number
  speedVariance: number
  life: number
  lifeVariance: number
  size: number
  sizeVariance: number
  color: number
  gravity?: number
  fadeOut?: boolean
  shrink?: boolean
  direction?: number  // radians, undefined = all directions
  directionVariance?: number
}

export class ParticleSystem {
  private container: Container
  private particles: Particle[] = []
  private pool: Graphics[] = []
  private maxParticles = 1000

  constructor(container: Container) {
    this.container = container
  }

  emit(config: ParticleEmitterConfig): void {
    for (let i = 0; i < config.count; i++) {
      if (this.particles.length >= this.maxParticles) break

      const particle = this.createParticle(config)
      this.particles.push(particle)
      this.container.addChild(particle.graphic)
    }
  }

  private createParticle(config: ParticleEmitterConfig): Particle {
    // Get or create graphic
    let graphic = this.pool.pop()
    if (!graphic) {
      graphic = new Graphics()
    }

    const size = config.size + randomRange(-config.sizeVariance, config.sizeVariance)
    const life = config.life + randomRange(-config.lifeVariance, config.lifeVariance)

    // Calculate velocity
    let angle: number
    if (config.direction !== undefined) {
      angle = config.direction + randomRange(
        -(config.directionVariance || 0),
        config.directionVariance || 0
      )
    } else {
      angle = randomRange(0, Math.PI * 2)
    }

    const speed = config.speed + randomRange(-config.speedVariance, config.speedVariance)
    const vx = Math.cos(angle) * speed
    const vy = Math.sin(angle) * speed

    // Position with spread
    const x = config.x + randomRange(-config.spread, config.spread)
    const y = config.y + randomRange(-config.spread, config.spread)

    // Draw particle
    graphic.clear()
    graphic.circle(0, 0, size)
    graphic.fill({ color: config.color })
    graphic.position.set(x, y)
    graphic.alpha = 1

    return {
      x,
      y,
      vx,
      vy,
      life,
      maxLife: life,
      size,
      color: config.color,
      alpha: 1,
      graphic
    }
  }

  update(dt: number, gravity: number = 0): void {
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i]

      // Update position
      p.x += p.vx * dt
      p.y += p.vy * dt
      p.vy += gravity * dt

      // Update life
      p.life -= dt

      // Update alpha (fade out)
      const lifeRatio = p.life / p.maxLife
      p.alpha = lifeRatio

      // Update graphic
      p.graphic.position.set(p.x, p.y)
      p.graphic.alpha = p.alpha
      p.graphic.scale.set(lifeRatio)

      // Remove dead particles
      if (p.life <= 0) {
        this.container.removeChild(p.graphic)
        this.pool.push(p.graphic)
        this.particles.splice(i, 1)
      }
    }
  }

  // Preset effects
  emitExplosion(x: number, y: number, color: number, size: number = 1): void {
    this.emit({
      x,
      y,
      count: Math.floor(20 * size),
      spread: 5,
      speed: 150 * size,
      speedVariance: 50,
      life: 0.5,
      lifeVariance: 0.2,
      size: 4 * size,
      sizeVariance: 2,
      color,
      gravity: 100
    })
  }

  emitSpark(x: number, y: number, color: number): void {
    this.emit({
      x,
      y,
      count: 5,
      spread: 2,
      speed: 80,
      speedVariance: 30,
      life: 0.3,
      lifeVariance: 0.1,
      size: 2,
      sizeVariance: 1,
      color,
      direction: -Math.PI / 2,  // Up
      directionVariance: Math.PI / 4
    })
  }

  emitGather(x: number, y: number, color: number): void {
    this.emit({
      x,
      y,
      count: 3,
      spread: 8,
      speed: 20,
      speedVariance: 10,
      life: 0.5,
      lifeVariance: 0.2,
      size: 3,
      sizeVariance: 1,
      color,
      direction: -Math.PI / 2,
      directionVariance: Math.PI / 6
    })
  }

  emitTrail(x: number, y: number, color: number): void {
    this.emit({
      x,
      y,
      count: 1,
      spread: 2,
      speed: 10,
      speedVariance: 5,
      life: 0.3,
      lifeVariance: 0.1,
      size: 2,
      sizeVariance: 0.5,
      color
    })
  }

  emitConstruction(x: number, y: number): void {
    this.emit({
      x,
      y,
      count: 3,
      spread: 20,
      speed: 30,
      speedVariance: 15,
      life: 0.6,
      lifeVariance: 0.2,
      size: 3,
      sizeVariance: 1,
      color: 0xffd700,
      direction: -Math.PI / 2,
      directionVariance: Math.PI / 3
    })
  }

  emitHeal(x: number, y: number): void {
    this.emit({
      x,
      y,
      count: 8,
      spread: 15,
      speed: 40,
      speedVariance: 20,
      life: 0.8,
      lifeVariance: 0.3,
      size: 4,
      sizeVariance: 2,
      color: 0x2ecc71,
      direction: -Math.PI / 2,
      directionVariance: Math.PI / 6
    })
  }

  emitDeath(x: number, y: number, color: number): void {
    this.emit({
      x,
      y,
      count: 30,
      spread: 10,
      speed: 100,
      speedVariance: 50,
      life: 0.8,
      lifeVariance: 0.3,
      size: 5,
      sizeVariance: 3,
      color,
      gravity: 200
    })
  }

  emitProjectile(x: number, y: number, color: number): void {
    this.emit({
      x,
      y,
      count: 2,
      spread: 1,
      speed: 5,
      speedVariance: 3,
      life: 0.15,
      lifeVariance: 0.05,
      size: 2,
      sizeVariance: 0.5,
      color
    })
  }

  clear(): void {
    for (const p of this.particles) {
      this.container.removeChild(p.graphic)
      this.pool.push(p.graphic)
    }
    this.particles = []
  }

  destroy(): void {
    this.clear()
    for (const g of this.pool) {
      g.destroy()
    }
    this.pool = []
  }
}
